import React, { useRef, useEffect, useState } from "react";
import { ChevronsRight, Sparkles } from "lucide-react";

const MAIN_IMG =
  "https://images.unsplash.com/photo-1611490135455-3a02bb9eb653?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHw0fHx0aHJvdyUyMHBpbGxvd3MlMjB3YXJtJTIwdG9uZXN8ZW58MHx8fHwxNzg1Njc5NjI0fDA&ixlib=rb-4.1.0&q=85&w=1400";

// Reveal on scroll wrapper
const Reveal = ({ children, delay = 0, className = "", from = "up" }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  const initial =
    from === "left"
      ? "translateX(-30px)"
      : from === "right"
      ? "translateX(30px)"
      : "translateY(28px)";
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translate(0,0)" : initial,
        transition: `opacity 900ms ease ${delay}ms, transform 900ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

const CountYears = ({ end }) => {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const [started, setStarted] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started) {
          setStarted(true);
          const start = performance.now();
          const step = (t) => {
            const p = Math.min(1, (t - start) / 1400);
            setValue(Math.round(end * (1 - Math.pow(1 - p, 3))));
            if (p < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      });
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [end, started]);
  return <span ref={ref}>{value}</span>;
};

const AboutAmber = () => {
  const containerRef = useRef(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  const onMouseMove = (e) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setMouse({ x: px, y: py });
  };
  const onMouseLeave = () => setMouse({ x: 0, y: 0 });

  const scrollToInfra = () => {
    const el = document.querySelector("#infrastructure");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const values = [
    "Design-Led",
    "Vertically Integrated",
    "Ethically Made",
    "Global Ready",
    "Craft with Care",
    "Quality First",
    "Trusted Partner",
    "Made in Karur",
  ];

  return (
    <section
      id="story"
      className="w-full scroll-mt-20 relative overflow-hidden"
      style={{ backgroundColor: "#FBF6EA" }}
    >
      {/* Animated ambient background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-32 -left-32 w-[460px] h-[460px] rounded-full opacity-50 story-blob"
          style={{
            background:
              "radial-gradient(circle, rgba(242,140,31,0.22) 0%, rgba(242,140,31,0) 70%)",
          }}
        />
        <div
          className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full opacity-40 story-blob-2"
          style={{
            background:
              "radial-gradient(circle, rgba(178,38,38,0.15) 0%, rgba(178,38,38,0) 70%)",
          }}
        />
        <svg
          className="absolute inset-0 w-full h-full opacity-[0.08] story-threads"
          viewBox="0 0 1200 800"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            d="M0,220 Q300,150 600,220 T1200,220"
            stroke="#B22626"
            strokeWidth="1.2"
            fill="none"
          />
          <path
            d="M0,540 Q300,600 600,540 T1200,540"
            stroke="#F28C1F"
            strokeWidth="1.2"
            fill="none"
          />
          <path
            d="M0,400 Q300,340 600,400 T1200,400"
            stroke="#8C5A2B"
            strokeWidth="0.9"
            fill="none"
          />
        </svg>
      </div>

      <div
        ref={containerRef}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        className="relative max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-24"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
          {/* LEFT: text */}
          <div className="max-w-lg">
            <Reveal from="left">
              <div
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-widest uppercase mb-5"
                style={{ backgroundColor: "#FEF2F2", color: "#B22626" }}
              >
                <Sparkles className="w-3.5 h-3.5" />
                The Amber Story
              </div>
            </Reveal>

            <Reveal from="left" delay={80}>
              <h2
                className="story-title-shimmer mb-6 leading-tight"
                style={{
                  fontFamily:
                    "'Playfair Display', Georgia, 'Times New Roman', serif",
                  fontSize: "clamp(2rem, 3.8vw, 3rem)",
                  fontWeight: 700,
                }}
              >
                About Amber
              </h2>
            </Reveal>

            <Reveal from="left" delay={180}>
              <p className="text-slate-700 leading-relaxed mb-5 text-[15px] md:text-base">
                Amber Living is a{" "}
                <span
                  className="font-semibold"
                  style={{ color: "#8C1F1F" }}
                >
                  vertically integrated home textile manufacturer
                </span>{" "}
                based in Karur, India, delivering thoughtfully crafted textile
                solutions for global home brands.
              </p>
            </Reveal>

            <Reveal from="left" delay={280}>
              <p className="text-slate-700 leading-relaxed mb-7 text-[15px] md:text-base">
                With strong manufacturing capabilities, in-house design
                expertise, and a commitment to quality, Amber partners with
                customers through{" "}
                <span
                  className="font-semibold"
                  style={{ color: "#8C1F1F" }}
                >
                  reliability, transparency, and lasting value creation
                </span>
                .
              </p>
            </Reveal>

            {/* Live mini stats removed per request */}

            <Reveal from="left" delay={380}>
              <button
                type="button"
                onClick={scrollToInfra}
                className="group inline-flex items-center gap-2 font-semibold text-[15px] transition-all duration-200 bg-transparent border-0 p-0 cursor-pointer"
                style={{ color: "#B22626" }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.color = "#8C1F1F")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.color = "#B22626")
                }
              >
                <span className="relative">
                  Learn About Amber
                  <span
                    className="absolute left-0 -bottom-0.5 h-[2px] rounded-full transition-all duration-300 group-hover:w-full w-0"
                    style={{ backgroundColor: "#B22626" }}
                  />
                </span>
                <ChevronsRight
                  className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1"
                  strokeWidth={2.5}
                />
              </button>
            </Reveal>
          </div>

          {/* RIGHT: interactive image cluster */}
          <Reveal from="right" delay={100}>
            <div
              className="relative aspect-[4/5] md:aspect-square lg:aspect-[5/6]"
              style={{ perspective: "1200px" }}
            >
              {/* Backdrop card */}
              <div
                aria-hidden="true"
                className="absolute rounded-lg story-backdrop-float"
                style={{
                  backgroundColor: "#EDE4CE",
                  top: "6%",
                  left: "6%",
                  right: "-6%",
                  bottom: "-6%",
                  transform: `translate3d(${mouse.x * -18}px, ${mouse.y * -18}px, 0)`,
                  transition: "transform 300ms ease-out",
                }}
              />
              {/* Main product image */}
              <div
                className="relative rounded-lg overflow-hidden bg-white z-10 w-full h-full"
                style={{
                  boxShadow:
                    "0 30px 60px -25px rgba(42, 18, 51, 0.35), 0 15px 30px -12px rgba(42, 18, 51, 0.2)",
                  transform: `translate3d(${mouse.x * 14}px, ${mouse.y * 14}px, 0) rotateX(${mouse.y * -3}deg) rotateY(${mouse.x * 3}deg)`,
                  transition: "transform 300ms ease-out",
                  transformStyle: "preserve-3d",
                }}
              >
                <img
                  src={MAIN_IMG}
                  alt="Amber Living — decorative home textiles"
                  className="w-full h-full object-cover story-main-kenburns"
                  loading="lazy"
                  draggable={false}
                />
              </div>

              {/* Floating accent cards removed per request */}

              {/* Decorative sparkle badge */}
              <div
                className="absolute top-4 left-4 z-30 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-md story-badge-pulse"
                style={{
                  backgroundColor: "rgba(178,38,38,0.85)",
                  color: "#ffffff",
                }}
              >
                <Sparkles className="w-3 h-3" />
                <span className="text-[10.5px] font-semibold tracking-widest uppercase">
                  Made with Care
                </span>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Bottom infinite marquee of values */}
        <Reveal delay={400}>
          <div className="mt-16 md:mt-20 relative">
            <div
              className="absolute inset-y-0 left-0 w-16 md:w-24 z-10 pointer-events-none"
              style={{
                background:
                  "linear-gradient(90deg, #FBF6EA 0%, rgba(251,246,234,0) 100%)",
              }}
            />
            <div
              className="absolute inset-y-0 right-0 w-16 md:w-24 z-10 pointer-events-none"
              style={{
                background:
                  "linear-gradient(-90deg, #FBF6EA 0%, rgba(251,246,234,0) 100%)",
              }}
            />
            <div className="overflow-hidden">
              <div className="flex whitespace-nowrap story-marquee">
                {[...values, ...values, ...values].map((v, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-4 md:gap-6 px-4 md:px-6"
                  >
                    <span
                      className="text-[15px] md:text-[18px] font-semibold tracking-[0.15em] uppercase"
                      style={{
                        fontFamily: "'Playfair Display', Georgia, serif",
                        color: "#0F1C3A",
                      }}
                    >
                      {v}
                    </span>
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: "#F28C1F" }}
                    />
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default AboutAmber;
